import { makeStyles } from '@material-ui/styles'
import Text from 'core/elements/Text'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import DropdownField from 'core/components/validatedForm/DropdownField'
import TextField from 'core/components/validatedForm/TextField'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import Badge from 'core/elements/badge/Badge'
import AsyncDropdown from 'core/elements/dropdown/AsyncDropdown'
import Tooltip from 'core/elements/tooltip/Tooltip'
import Theme from 'core/themes/model'
import React from 'react'

const BACKUP_SCHEDULE = [
  { label: 'Hourly', value: 'hourly' },
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
]

const ALERT_METHODS = [
  { label: 'Webhook', value: 'webhook' },
  { label: 'Slack', value: 'slack' },
  { label: 'Teams', value: 'teams' },
  { label: 'PagerDuty', value: 'pagerduty' },
  { label: 'VictorOps', value: 'victorops' },
]

// TODO: Move AddonFieldProps to pf9-ui-components. It's used in multiple places.
interface AddonFieldProps {
  id: string
  label: string
  description?: string
  params: Record<any, any>
  getParamsUpdater: (value: any) => void
  disabled?: boolean
  badgeText?: string
  tooltipMsg?: string
  info?: string
  customTooltipInfoBody?: React.ReactNode
}

const AddonField = ({
  id,
  label,
  description,
  params,
  getParamsUpdater,
  disabled,
  children,
  badgeText,
  tooltipMsg,
  info,
  customTooltipInfoBody,
}: React.PropsWithChildren<AddonFieldProps>) => {
  const classes = useAddonFieldStyles()
  const showInfoCircleIcon = info || customTooltipInfoBody

  const ToggleComponent = (
    <ToggleSwitchField
      id={id}
      value={params[id]}
      onChange={getParamsUpdater(id)}
      disabled={disabled}
    />
  )
  return (
    <div className={classes.addonContainer}>
      {tooltipMsg ? (
        <Tooltip message={tooltipMsg}>{ToggleComponent}</Tooltip>
      ) : (
        <>{ToggleComponent}</>
      )}
      <div>
        <div className={classes.label}>
          <Text variant="caption1">{label}</Text>
          {showInfoCircleIcon && (
            <Tooltip customBody={customTooltipInfoBody} message={info}>
              <FontAwesomeIcon>circle-info</FontAwesomeIcon>
            </Tooltip>
          )}
        </div>
        {badgeText && <Badge className={classes.badge} text={badgeText} variant="default" />}
      </div>
      <div className={classes.desciptionAndOptions}>
        <Text variant="body2">{description}</Text>
        {children}
      </div>
    </div>
  )
}

const useAddonFieldStyles = makeStyles<Theme>((theme) => ({
  addonContainer: {
    display: 'grid',
    gridTemplateColumns: 'max-content 131px 1fr',
    gridGap: theme.spacing(4),
    alignItems: 'start',
  },
  desciptionAndOptions: {
    display: 'grid',
    gridAutoFlow: 'row',
    gridGap: theme.spacing(3),
  },
  label: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridGap: theme.spacing(1),
  },
  badge: {
    marginTop: theme.spacing(2),
    backgroundColor: '#ECFBFA',
    border: '1px solid #ECFBFA',
  },
}))

interface CloudspadceConfigurationFieldsProps {
  params: Record<any, any>
  getParamsUpdater: (key: string) => (value: any) => void
}

export default function CustomOptionsFields({
  params,
  getParamsUpdater,
}: CloudspadceConfigurationFieldsProps) {
  const classes = useStyles()

  return (
    <div className={classes.clusterConfigFields}>
      <AddonField
        id="enableBackups"
        label="Backups"
        description="Automatically backup this database cluster."
        params={params}
        getParamsUpdater={getParamsUpdater}
      >
        {params.enableBackups && (
          <div className={classes.backupFields}>
            <DropdownField
              id="backupSchedule"
              label="Backup Schedule"
              DropdownComponent={AsyncDropdown}
              items={BACKUP_SCHEDULE}
              value={params.backupSchedule}
              onChange={getParamsUpdater('backupSchedule')}
              required
              selectFirst
            />
            <TextField
              id="numBackups"
              label="Backups Retained"
              value={params.numBackups}
              onChange={getParamsUpdater('numBackups')}
              required
              type="number"
              min={1}
              nonce={undefined}
              onResize={undefined}
              onResizeCapture={undefined}
              enterKeyHint={undefined}
            />
          </div>
        )}
      </AddonField>
      <hr className={classes.hr} />
      <AddonField
        id="enableAlerts"
        label="Alerts"
        description="Alert me on cluster health events that affect application uptime or responsiveness."
        params={params}
        getParamsUpdater={getParamsUpdater}
      >
        {params.enableAlerts && (
          <DropdownField
            id="alertMethod"
            DropdownComponent={AsyncDropdown}
            items={ALERT_METHODS}
            value={params.alertMethod}
            onChange={getParamsUpdater('alertMethod')}
            required
            selectFirst
          />
        )}
      </AddonField>
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  clusterConfigFields: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridGap: theme.spacing(3),
    margin: theme.spacing(2, 0, 4, 0),
  },
  hr: {
    height: 1,
    background: theme.components.card.border,
    border: 0,
    margin: theme.spacing(2, 3, 1, 0),
  },
  backupFields: {
    display: 'grid',
    gridTemplateColumns: '400px 152px',
    gridGap: theme.spacing(3),
    alignItems: 'end',
  },
}))
